import { useTranslations } from "@zapier/i18n";
import { InterfacePage, useInterfacePages } from "@zapier/client";
import { Select, SelectItem } from "@zapier/design-system";
import { useEffect, useMemo } from "react";
import { Controller, useFormContext } from "react-hook-form";

interface PageToEmbedFieldProps {
  interfaceId: string;
  loginPageId?: string;
}

function useNonLoginPages(
  interfaceId: string,
  loginPageId?: string,
): {
  pages: InterfacePage[];
} {
  const { pages } = useInterfacePages(
    { id: interfaceId },
    { enabled: Boolean(interfaceId) },
  );

  const filteredPages = useMemo(
    () => pages?.filter((page) => page.id !== loginPageId) || [],
    [pages, loginPageId],
  );

  return {
    pages: filteredPages,
  };
}

export function PageToEmbedField({
  interfaceId,
  loginPageId,
}: PageToEmbedFieldProps) {
  const t = useTranslations("page-embed-field");

  const { control, setValue, trigger, watch } = useFormContext();

  const { pages } = useNonLoginPages(interfaceId, loginPageId);

  // Set initial pageId if available
  useEffect(() => {
    const pageId = watch("pageId");
    if (!pageId && pages.length > 0) {
      setValue("pageId", pages[0].id);
    }
  }, [pages, setValue, watch]);

  return (
    <Controller
      control={control}
      name="pageId"
      render={({ field }) => (
        <Select
          label={t("labels.page-to-embed")}
          selectedKey={field.value ?? undefined}
          onChange={(key) => {
            field.onChange((key as string) || "");
            trigger("pageId");
          }}
        >
          {pages.map((page) => (
            <SelectItem key={page.id} value={page.id}>
              {page.name ?? page.title}
            </SelectItem>
          ))}
        </Select>
      )}
    />
  );
}
